<template>
  <div class="tw-text-left">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
      <h2 class="tw-text-base tw-font-semibold tw-text-slate-900">Reefer details</h2>
      <Button
        v-if="editable"
        @click="isEditReeferDetailsModalOpen = true"
        icon="pen-to-square"
        title="Edit reefer details"
        aria-label="Edit reefer details"
      ></Button>
    </div>

    <dl class="tw-mt-3 tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-2">
      <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-p-2.5">
        <dt class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Genset: Needed</dt>
        <dd class="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-900">{{ container.isGensetRequired ? 'Yes' : 'No' }}</dd>
      </div>
      <div v-if="container.isGensetRequired" class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-p-2.5">
        <dt class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Genset: Approved by shipline</dt>
        <dd class="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-900">
          <span v-if="container.isGensetApprovedByShipline === true">Yes</span>
          <span v-else-if="container.isGensetApprovedByShipline === false">No</span>
          <span v-else class="tw-text-slate-500">—</span>
        </dd>
      </div>
      <div v-if="container.isGensetRequired" class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-p-2.5">
        <dt class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Genset Number</dt>
        <dd class="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-900">
          <span v-if="container.gensetNumber">{{ container.gensetNumber }}</span>
          <span v-else class="tw-text-slate-500">—</span>
        </dd>
      </div>
    </dl>
    <dl class="tw-mt-3 tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-2">
      <!-- Temperature and vents apply to every reefer, also when it runs on in-house power -->
      <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-p-2.5">
        <dt class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Genset Temperature</dt>
        <dd class="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-900">
          <span v-if="enteredTemperatureLabel">{{ enteredTemperatureLabel }} ({{  convertedTemperatureHint }})</span>
          <span v-else class="tw-text-slate-500">—</span>
        </dd>
      </div>
      <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50 tw-p-2.5">
        <dt class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Genset Vents</dt>
        <dd class="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-slate-900">
          <span v-if="hasVentsOpenPercent">{{ container.gensetVentsOpenPercent }}% open</span>
          <span v-else class="tw-text-slate-500">—</span>
        </dd>
      </div>
    </dl>

    <ContainerReeferDetailsEditModal
      v-model:isOpen="isEditReeferDetailsModalOpen"
      :container="container"
      @saved="$emit('saved', $event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import Button from '@/components/core/Button.vue';
import ContainerReeferDetailsEditModal from './ContainerReeferDetailsEditModal.vue';


function convertToCelsius(value: number, unit: string): number {
  return unit === 'C' ? value : (value - 32) * 5 / 9;
}

function convertToFahrenheit(value: number, unit: string): number {
  return unit === 'F' ? value : value * 9 / 5 + 32;
}

export default defineComponent({
  name: 'ContainerReeferDetails',
  components: {
    Button,
    ContainerReeferDetailsEditModal,
  },
  props: {
    // Needs id + the reeferDetails fragment fields
    container: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['saved'],
  data() {
    return {
      isEditReeferDetailsModalOpen: false,
    };
  },
  computed: {
    // Shown in the unit it was entered in (as it appears on the source document)
    enteredTemperatureLabel(): string {
      const container = (this as any).container;
      const unit = container.gensetTemperatureUnit;
      const value = unit === 'C' ? container.gensetTemperatureC : container.gensetTemperatureF;
      if (!unit || value === null || value === undefined) return '';
      return `${value} °${unit}`;
    },
    convertedTemperatureHint(): string {
      const vm = this as any;
      const container = vm.container;
      const unit = container.gensetTemperatureUnit;
      const value = unit === 'C' ? container.gensetTemperatureC : container.gensetTemperatureF;
      if (!unit || value === null || value === undefined) return '';
      if (unit === 'C') {
        return `${convertToFahrenheit(value, unit).toFixed(1)} °F`;
      }
      return `${convertToCelsius(value, unit).toFixed(1)} °C`;
    },
    hasVentsOpenPercent(): boolean {
      const percent = (this as any).container.gensetVentsOpenPercent;
      return percent !== null && percent !== undefined;
    },
  },
});
</script>
